import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Clock, Calendar, Users, CheckCircle, XCircle, Plus, Award,
  MapPin, Loader2, Filter, Download, TrendingUp, Target, Edit2, Trash2, X
} from 'lucide-react';
import { COLORS } from '../../../constants/dashboard';
import ProgressRing from '../shared/ProgressRing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VolunteerView = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'super_admin' || user?.role === 'business_admin';
  const [activeTab, setActiveTab] = useState(isAdmin ? 'events' : 'my-hours');
  const [myHours, setMyHours] = useState(null);
  const [events, setEvents] = useState([]);
  const [allLogs, setAllLogs] = useState([]);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEditLogModal, setShowEditLogModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editEventForm, setEditEventForm] = useState({
    title: '',
    description: '',
    charity_name: '',
    location: '',
    event_date: '',
    start_time: '',
    end_time: '',
    max_volunteers: '',
    hours_value: 4,
    status: 'upcoming'
  });

  const [logForm, setLogForm] = useState({
    activity_name: '',
    charity_name: '',
    hours: '',
    activity_date: new Date().toISOString().split('T')[0],
    description: ''
  });

  const [editLogForm, setEditLogForm] = useState({
    activity_name: '',
    charity_name: '',
    hours: '',
    activity_date: '',
    description: '',
    status: 'pending'
  });

  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    charity_name: '',
    location: '',
    event_date: '',
    start_time: '',
    end_time: '',
    max_volunteers: '',
    hours_value: 4
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [hoursRes, eventsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/volunteer/my-hours`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/volunteer/events`, { withCredentials: true })
      ]);
      setMyHours(hoursRes.data);
      setEvents(eventsRes.data.events || []);

      if (isAdmin) {
        const logsRes = await axios.get(`${BACKEND_URL}/api/volunteer/logs`, { withCredentials: true });
        setAllLogs(logsRes.data.logs || []);
      }
    } catch (err) {
      console.error('Failed to fetch volunteer data:', err);
    } finally {
      setLoading(false);
    }
  };

  const submitHours = async () => {
    if (!logForm.activity_name || !logForm.hours) {
      alert('Please fill in required fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/volunteer/log`, logForm, { withCredentials: true });
      setShowLogModal(false);
      setLogForm({ activity_name: '', charity_name: '', hours: '', activity_date: new Date().toISOString().split('T')[0], description: '' });
      fetchData();
    } catch (err) {
      alert('Failed to submit hours');
    } finally {
      setSubmitting(false);
    }
  };

  const createEvent = async () => {
    if (!eventForm.title || !eventForm.event_date) {
      alert('Please fill in required fields');
      return;
    }
    setSubmitting(true);
    try {
      // Clean up form data - remove empty strings
      const cleanedForm = {
        title: eventForm.title,
        event_date: eventForm.event_date,
        hours_value: eventForm.hours_value || 4
      };
      if (eventForm.description) cleanedForm.description = eventForm.description;
      if (eventForm.charity_name) cleanedForm.charity_name = eventForm.charity_name;
      if (eventForm.location) cleanedForm.location = eventForm.location;
      if (eventForm.start_time) cleanedForm.start_time = eventForm.start_time;
      if (eventForm.end_time) cleanedForm.end_time = eventForm.end_time;
      if (eventForm.max_volunteers) cleanedForm.max_volunteers = parseInt(eventForm.max_volunteers);
      
      await axios.post(`${BACKEND_URL}/api/volunteer/events`, cleanedForm, { withCredentials: true });
      setShowEventModal(false);
      setEventForm({ title: '', description: '', charity_name: '', location: '', event_date: '', start_time: '', end_time: '', max_volunteers: '', hours_value: 4 });
      fetchData();
    } catch (err) {
      console.error('Create event error:', err);
      alert(err.response?.data?.detail || 'Failed to create event');
    } finally {
      setSubmitting(false);
    }
  };

  const approveLog = async (logId, approved) => {
    try {
      await axios.put(`${BACKEND_URL}/api/volunteer/logs/${logId}/approve?approved=${approved}`, {}, { withCredentials: true });
      fetchData();
    } catch (err) {
      alert('Failed to update log');
    }
  };

  const handleEditLog = (log) => {
    setEditingLog(log);
    setEditLogForm({
      activity_name: log.activity_name || '',
      charity_name: log.charity_name || '',
      hours: log.hours || '',
      activity_date: log.activity_date?.split('T')[0] || '',
      description: log.description || '',
      status: log.status || 'pending'
    });
    setShowEditLogModal(true);
  };

  const saveEditLog = async () => {
    if (!editingLog) return;
    setSubmitting(true);
    try {
      await axios.put(`${BACKEND_URL}/api/volunteer/logs/${editingLog.log_id}`, editLogForm, { withCredentials: true });
      setShowEditLogModal(false);
      setEditingLog(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update log');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteLog = async (logId) => {
    if (!window.confirm('Are you sure you want to delete this volunteer log?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/volunteer/logs/${logId}`, { withCredentials: true });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete log');
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEditEventForm({
      title: event.title || '',
      description: event.description || '',
      charity_name: event.charity_name || '',
      location: event.location || '',
      event_date: event.event_date?.split('T')[0] || '',
      start_time: event.start_time || '',
      end_time: event.end_time || '',
      max_volunteers: event.max_volunteers || '',
      hours_value: event.hours_value || 4,
      status: event.status || 'upcoming'
    });
    setShowEditEventModal(true);
  };

  const saveEditEvent = async () => {
    if (!editingEvent) return;
    setSubmitting(true);
    try {
      await axios.put(`${BACKEND_URL}/api/volunteer/events/${editingEvent.event_id}`, editEventForm, { withCredentials: true });
      setShowEditEventModal(false);
      setEditingEvent(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update event');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this volunteer opportunity?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/volunteer/events/${eventId}`, { withCredentials: true });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete event');
    }
  };

  const registerForEvent = async (eventId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/volunteer/events/${eventId}/register`, {}, { withCredentials: true });
      alert('Registered successfully!');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to register');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const pendingLogs = allLogs.filter(l => l.status === 'pending');

  return (
    <div className="p-4" style={{ background: COLORS.background }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold" style={{ color: COLORS.primary }}>Volunteer Tracking</h2>
          <p className="text-sm text-gray-500">
            {isAdmin ? 'Manage volunteer events and review employee submissions' : 'Log hours, join events, and track your PTO balance'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin ? (
            <button
              onClick={() => setShowEventModal(true)}
              className="px-4 py-2 text-white text-sm rounded-lg font-medium flex items-center gap-2"
              style={{ background: COLORS.primary }}
            >
              <Plus className="w-4 h-4" /> Create Event
            </button>
          ) : (
            <button
              onClick={() => setShowLogModal(true)}
              className="px-4 py-2 text-white text-sm rounded-lg font-medium flex items-center gap-2"
              style={{ background: COLORS.primary }}
            >
              <Plus className="w-4 h-4" /> Log Hours
            </button>
          )}
        </div>
      </div>

      {/* Stats Row - Different for Admin vs Employee */}
      {isAdmin ? (
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-5 h-5" style={{ color: COLORS.primary }} />
              <span className="text-xs text-gray-500">Active</span>
            </div>
            <p className="text-xl font-bold" style={{ color: COLORS.primary }}>{events.filter(e => e.status === 'upcoming' || e.status === 'ongoing').length}</p>
            <p className="text-xs text-gray-500">Volunteer Events</p>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-5 h-5 text-amber-500" />
              <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full">Review</span>
            </div>
            <p className="text-xl font-bold text-amber-600">{pendingLogs.length}</p>
            <p className="text-xs text-gray-500">Pending Approvals</p>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <TrendingUp className="w-3 h-3 text-green-500" />
            </div>
            <p className="text-xl font-bold text-green-600">{allLogs.filter(l => l.status === 'approved').length}</p>
            <p className="text-xs text-gray-500">Approved Logs</p>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-5 h-5" style={{ color: COLORS.secondary }} />
              <span className="text-xs text-gray-500">Total</span>
            </div>
            <p className="text-xl font-bold" style={{ color: COLORS.secondary }}>{allLogs.reduce((sum, l) => sum + (l.status === 'approved' ? (l.hours || 0) : 0), 0)}h</p>
            <p className="text-xs text-gray-500">Company Hours</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-5 gap-3 mb-4">
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-5 h-5 text-purple-500" />
              <span className="text-xs text-gray-500">Approved</span>
            </div>
            <p className="text-xl font-bold text-purple-600">{myHours?.summary?.approved_hours || 0}h</p>
            <p className="text-xs text-gray-500">Total Hours</p>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-5 h-5 text-amber-500" />
              <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full">Pending</span>
            </div>
            <p className="text-xl font-bold text-amber-600">{myHours?.summary?.pending_hours || 0}h</p>
            <p className="text-xs text-gray-500">Awaiting Approval</p>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Target className="w-5 h-5 text-green-500" />
              <TrendingUp className="w-3 h-3 text-green-500" />
            </div>
            <p className="text-xl font-bold text-green-600">{myHours?.summary?.pto_balance || 0}h</p>
            <p className="text-xs text-gray-500">PTO Balance</p>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Award className="w-5 h-5" style={{ color: COLORS.secondary }} />
              <span className="text-xs text-gray-500">Cap</span>
            </div>
            <p className="text-xl font-bold" style={{ color: COLORS.secondary }}>{myHours?.summary?.pto_cap || 0}h</p>
            <p className="text-xs text-gray-500">Annual Cap</p>
          </div>
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-5 h-5" style={{ color: COLORS.primary }} />
              <span className="text-xs text-gray-500">Events</span>
            </div>
            <p className="text-xl font-bold" style={{ color: COLORS.primary }}>{events.length}</p>
            <p className="text-xs text-gray-500">Upcoming</p>
          </div>
        </div>
      )}

      {/* Tabs - Different for Admin vs Employee */}
      <div className="flex gap-2 mb-4">
        {isAdmin ? (
          // Admin tabs: Events, All Logs, Approvals
          [
            { id: 'events', label: `Volunteer Events (${events.length})` },
            { id: 'all-logs', label: 'All Employee Logs' },
            { id: 'approvals', label: `Pending Approvals (${pendingLogs.length})` }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                activeTab === tab.id
                  ? 'text-white shadow-md'
                  : 'bg-white text-gray-600 border hover:bg-gray-50'
              }`}
              style={activeTab === tab.id ? { background: COLORS.primary } : {}}
            >
              {tab.label}
            </button>
          ))
        ) : (
          // Employee tabs: My Hours, Events
          [
            { id: 'my-hours', label: 'My Hours' },
            { id: 'events', label: 'Volunteer Events' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                activeTab === tab.id
                  ? 'text-white shadow-md'
                  : 'bg-white text-gray-600 border hover:bg-gray-50'
              }`}
              style={activeTab === tab.id ? { background: COLORS.primary } : {}}
            >
              {tab.label}
            </button>
          ))
        )}
      </div>

      {/* My Hours Tab - Only for Employees */}
      {activeTab === 'my-hours' && !isAdmin && (
        <div className="grid grid-cols-12 gap-4">
          {/* Hours List */}
          <div className="col-span-8 bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ background: COLORS.light }}>
              <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>My Volunteer Hours</h3>
              <span className="text-xs text-gray-500">{myHours?.logs?.length || 0} entries</span>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {myHours?.logs?.length > 0 ? myHours.logs.map((log, i) => (
                <div key={log.log_id || i} className="px-4 py-3 border-b last:border-0 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${COLORS.primary}15` }}>
                        <Clock className="w-5 h-5" style={{ color: COLORS.primary }} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{log.activity_name}</p>
                        <p className="text-sm text-gray-500">{log.charity_name || 'Personal volunteering'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold" style={{ color: COLORS.primary }}>{log.hours}h</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        log.status === 'approved' ? 'bg-green-100 text-green-700' :
                        log.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {log.status}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">{log.activity_date}</p>
                </div>
              )) : (
                <div className="p-8 text-center text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No volunteer hours logged yet</p>
                  <button
                    onClick={() => setShowLogModal(true)}
                    className="mt-3 px-4 py-2 text-sm font-medium rounded-lg"
                    style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}
                  >
                    Log Your First Hours
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* PTO Balance Widget */}
          <div className="col-span-4 space-y-4">
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <h3 className="font-semibold text-sm mb-4" style={{ color: COLORS.accent }}>Volunteer PTO Balance</h3>
              <div className="flex items-center justify-center mb-4">
                <div className="relative">
                  <ProgressRing 
                    progress={myHours?.summary?.pto_cap > 0 ? ((myHours?.summary?.pto_balance || 0) / myHours.summary.pto_cap) * 100 : 0} 
                    size={120} 
                    strokeWidth={10} 
                    color={COLORS.success} 
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold" style={{ color: COLORS.accent }}>{myHours?.summary?.pto_balance || 0}h</span>
                    <span className="text-xs text-gray-500">available</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Annual Cap</span>
                  <span className="font-medium">{myHours?.summary?.pto_cap || 0}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Used</span>
                  <span className="font-medium text-orange-600">{myHours?.summary?.pto_used || 0}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Remaining</span>
                  <span className="font-medium text-green-600">{myHours?.summary?.pto_balance || 0}h</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border p-4">
              <h3 className="font-semibold text-sm mb-3" style={{ color: COLORS.accent }}>Quick Stats</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Total Approved</span>
                  <span className="font-bold text-purple-600">{myHours?.summary?.approved_hours || 0}h</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Pending Review</span>
                  <span className="font-bold text-amber-600">{myHours?.summary?.pending_hours || 0}h</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Events Tab */}
      {activeTab === 'events' && (
        <div className="grid grid-cols-3 gap-4">
          {events.length > 0 ? events.map(event => (
            <div key={event.event_id} className="bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-all">
              <div className="h-2" style={{ background: COLORS.primary }} />
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{event.title}</h4>
                    <p className="text-sm text-gray-500">{event.charity_name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      event.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                      event.status === 'ongoing' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {event.status}
                    </span>
                    {isAdmin && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEditEvent(event)}
                          className="p-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                          title="Edit Event"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => deleteEvent(event.event_id)}
                          className="p-1.5 bg-red-50 text-red-600 rounded hover:bg-red-100"
                          title="Delete Event"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {event.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{event.description}</p>
                )}
                
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>{event.event_date}</span>
                    {event.start_time && <span>at {event.start_time}</span>}
                  </div>
                  {event.location && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{event.location}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>{event.hours_value}h credit</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Users className="w-4 h-4" />
                    <span>{event.registered_count || 0}{event.max_volunteers ? `/${event.max_volunteers}` : ''} registered</span>
                  </div>
                </div>
                
                {/* Register button only for non-admins (employees) */}
                {!isAdmin && (
                  <button
                    onClick={() => registerForEvent(event.event_id)}
                    className="w-full py-2 text-white rounded-lg font-medium text-sm"
                    style={{ background: COLORS.primary }}
                  >
                    Register
                  </button>
                )}
              </div>
            </div>
          )) : (
            <div className="col-span-3 bg-white rounded-xl shadow-sm border p-8 text-center">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">No volunteer events scheduled</p>
              {isAdmin && (
                <button
                  onClick={() => setShowEventModal(true)}
                  className="mt-3 px-4 py-2 text-sm font-medium rounded-lg"
                  style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}
                >
                  Create First Event
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* All Employee Logs Tab (Admin Only) */}
      {activeTab === 'all-logs' && isAdmin && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ background: COLORS.light }}>
            <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>All Employee Volunteer Logs</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{allLogs.length} total entries</span>
              <button className="p-1.5 hover:bg-gray-100 rounded-lg" title="Export">
                <Download className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {allLogs.length > 0 ? allLogs.map(log => (
              <div key={log.log_id} className="px-4 py-3 border-b last:border-0 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: COLORS.primary }}>
                      {(log.user_name || log.user_email)?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{log.user_name || 'Unknown'}</p>
                      <p className="text-sm text-gray-600">{log.activity_name}</p>
                      <p className="text-xs text-gray-400">{log.charity_name || 'Personal volunteering'} • {log.activity_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-bold" style={{ color: COLORS.primary }}>{log.hours}h</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        log.status === 'approved' ? 'bg-green-100 text-green-700' :
                        log.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {log.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEditLog(log)}
                        className="p-1.5 hover:bg-blue-50 rounded-lg text-gray-400 hover:text-blue-600"
                        title="Edit log"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteLog(log.log_id)}
                        className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600"
                        title="Delete log"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )) : (
              <div className="p-8 text-center text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No volunteer logs from employees yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Approvals Tab (Admin Only) */}
      {activeTab === 'approvals' && isAdmin && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ background: COLORS.light }}>
            <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Pending Approvals</h3>
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">{pendingLogs.length} pending</span>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {pendingLogs.length > 0 ? pendingLogs.map(log => (
              <div key={log.log_id} className="px-4 py-3 border-b last:border-0 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{log.user_name || log.user_email}</p>
                    <p className="text-sm text-gray-600">{log.activity_name}</p>
                    <p className="text-xs text-gray-400">{log.activity_date} • {log.charity_name || 'Personal'}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-lg" style={{ color: COLORS.primary }}>{log.hours}h</span>
                    <button
                      onClick={() => approveLog(log.log_id, true)}
                      className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200"
                      title="Approve"
                    >
                      <CheckCircle className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => approveLog(log.log_id, false)}
                      className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                      title="Reject"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleEditLog(log)}
                      className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                      title="Edit"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => deleteLog(log.log_id)}
                      className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )) : (
              <div className="p-8 text-center text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                <p>All caught up! No pending approvals.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Log Hours Modal */}
      {showLogModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Log Volunteer Hours</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Activity Name *</label>
                <input
                  type="text"
                  value={logForm.activity_name}
                  onChange={(e) => setLogForm({...logForm, activity_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Food bank volunteering"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Charity/Organization</label>
                <input
                  type="text"
                  value={logForm.charity_name}
                  onChange={(e) => setLogForm({...logForm, charity_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Local Food Bank"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hours *</label>
                  <input
                    type="number"
                    value={logForm.hours}
                    onChange={(e) => setLogForm({...logForm, hours: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="0.5"
                    step="0.5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={logForm.activity_date}
                    onChange={(e) => setLogForm({...logForm, activity_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={logForm.description}
                  onChange={(e) => setLogForm({...logForm, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                  placeholder="What did you do?"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowLogModal(false)}
                className="flex-1 py-2 border rounded-lg font-medium text-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={submitHours}
                disabled={submitting}
                className="flex-1 py-2 text-white rounded-lg font-medium"
                style={{ background: COLORS.primary }}
              >
                {submitting ? 'Submitting...' : 'Submit for Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Create Volunteer Event</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Title *</label>
                <input
                  type="text"
                  value={eventForm.title}
                  onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Charity/Partner</label>
                <input
                  type="text"
                  value={eventForm.charity_name}
                  onChange={(e) => setEventForm({...eventForm, charity_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  value={eventForm.location}
                  onChange={(e) => setEventForm({...eventForm, location: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={eventForm.event_date}
                    onChange={(e) => setEventForm({...eventForm, event_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hours Credit</label>
                  <input
                    type="number"
                    value={eventForm.hours_value}
                    onChange={(e) => setEventForm({...eventForm, hours_value: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={eventForm.start_time}
                    onChange={(e) => setEventForm({...eventForm, start_time: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Volunteers</label>
                  <input
                    type="number"
                    value={eventForm.max_volunteers}
                    onChange={(e) => setEventForm({...eventForm, max_volunteers: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm({...eventForm, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEventModal(false)}
                className="flex-1 py-2 border rounded-lg font-medium text-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={createEvent}
                disabled={submitting}
                className="flex-1 py-2 text-white rounded-lg font-medium"
                style={{ background: COLORS.primary }}
              >
                {submitting ? 'Creating...' : 'Create Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Log Modal */}
      {showEditLogModal && editingLog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">Edit Volunteer Hours</h3>
              <button onClick={() => { setShowEditLogModal(false); setEditingLog(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Activity Name *</label>
                <input
                  type="text"
                  value={editLogForm.activity_name}
                  onChange={(e) => setEditLogForm({ ...editLogForm, activity_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization/Charity</label>
                <input
                  type="text"
                  value={editLogForm.charity_name}
                  onChange={(e) => setEditLogForm({ ...editLogForm, charity_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hours *</label>
                  <input
                    type="number"
                    step="0.5"
                    value={editLogForm.hours}
                    onChange={(e) => setEditLogForm({ ...editLogForm, hours: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={editLogForm.activity_date}
                    onChange={(e) => setEditLogForm({ ...editLogForm, activity_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editLogForm.description}
                  onChange={(e) => setEditLogForm({ ...editLogForm, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                />
              </div>
              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editLogForm.status}
                    onChange={(e) => setEditLogForm({ ...editLogForm, status: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              )}
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => { setShowEditLogModal(false); setEditingLog(null); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={saveEditLog}
                disabled={submitting}
                className="px-4 py-2 text-white rounded-lg flex items-center gap-2"
                style={{ background: COLORS.primary }}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditEventModal && editingEvent && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">Edit Volunteer Event</h3>
              <button onClick={() => { setShowEditEventModal(false); setEditingEvent(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Title *</label>
                <input
                  type="text"
                  value={editEventForm.title}
                  onChange={(e) => setEditEventForm({ ...editEventForm, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Charity/Partner</label>
                <input
                  type="text"
                  value={editEventForm.charity_name}
                  onChange={(e) => setEditEventForm({ ...editEventForm, charity_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  value={editEventForm.location}
                  onChange={(e) => setEditEventForm({ ...editEventForm, location: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={editEventForm.event_date}
                    onChange={(e) => setEditEventForm({ ...editEventForm, event_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hours Credit</label>
                  <input
                    type="number"
                    value={editEventForm.hours_value}
                    onChange={(e) => setEditEventForm({ ...editEventForm, hours_value: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={editEventForm.start_time}
                    onChange={(e) => setEditEventForm({ ...editEventForm, start_time: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Volunteers</label>
                  <input
                    type="number"
                    value={editEventForm.max_volunteers}
                    onChange={(e) => setEditEventForm({ ...editEventForm, max_volunteers: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={editEventForm.status}
                  onChange={(e) => setEditEventForm({ ...editEventForm, status: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="upcoming">Upcoming</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editEventForm.description}
                  onChange={(e) => setEditEventForm({ ...editEventForm, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                />
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => { setShowEditEventModal(false); setEditingEvent(null); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={saveEditEvent}
                disabled={submitting}
                className="px-4 py-2 text-white rounded-lg flex items-center gap-2"
                style={{ background: COLORS.primary }}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VolunteerView;
